import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData, QueryKey } from '@tanstack/react-query';
import { AdminUser } from '@app-types/admin';
import { PaginatedResponse, UserStatusTotals } from '@app-types';

interface UsersResponse extends PaginatedResponse<AdminUser> {
  statusTotals?: UserStatusTotals;
}

interface UseUserPageDataOptions {
  queryKey: QueryKey;
  queryFn: () => Promise<UsersResponse>;
  pageSize: number; // Required for correct totalPages calculation
  enabled?: boolean;
}

export interface UseUserPageDataReturn {
  users: AdminUser[];
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;
  response: UsersResponse | undefined;
  filterCounts: Record<string, number>;
  totalItems: number;
  totalPages: number;
  refreshData: () => void;
}

/**
 * Generic hook for data fetching in user management page
 * Handles: data fetching, statusTotals preservation, filter counts, pagination
 * Same implementation as useContractPageData and useAssessmentPageData
 */
export function useUserPageData(options: UseUserPageDataOptions): UseUserPageDataReturn {
  const { queryKey, queryFn, pageSize, enabled = true } = options;
  const queryClient = useQueryClient();

  const {
    data: response,
    isLoading,
    error,
    isFetching
  } = useQuery<UsersResponse>({
    queryKey,
    queryFn,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
    enabled
  });

  const users = useMemo(() => response?.data || [], [response]);

  // Preserve last known totals to avoid counter flicker
  const [lastStatusTotals, setLastStatusTotals] = useState<UserStatusTotals | null>(null);
  useEffect(() => {
    if (response?.statusTotals) {
      setLastStatusTotals(response.statusTotals);
    }
  }, [response?.statusTotals]);

  // Calculate filter counts from statusTotals
  const filterCounts: Record<string, number> = useMemo(() => {
    const totals = response?.statusTotals || lastStatusTotals;

    if (!totals) {
      return {
        all: response?.total || 0,
        active: 0,
        inactive: 0,
        contacts: 0,
        pending: 0,
        'pending-inactivation': 0
      };
    }

    // Use the backend-provided 'all' which includes active + inactive + contacts.
    // Computing active + inactive on the frontend excluded contacts (roleless users).
    const allCount = totals.all ?? (totals.active || 0) + (totals.inactive || 0);

    return {
      all: allCount || response?.total || 0,
      active: totals.active || 0,
      inactive: totals.inactive || 0,
      contacts: totals.contacts || 0,
      pending: totals.pending || 0,
      'pending-inactivation': totals.pendingInactivation || 0
    };
  }, [response, lastStatusTotals]);

  const totalItems = response?.total || 0;
  const totalPages = Math.ceil(totalItems / (pageSize || 1));

  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [queryKey[0]] });
  }, [queryClient, queryKey]);

  return {
    users,
    isLoading,
    error: error as Error | null,
    isFetching,
    response,
    filterCounts,
    totalItems,
    totalPages,
    refreshData
  };
}
